import { action as createAction } from "@iva/store-utils";
import {
    ActionIdentifiers,
    FetchActionFailedActionType,
    FetchActionStartedActionType,
    FetchActionSucceededActionType,
    FilterOperator,
    NlOperation,
    PutActionFailedActionType,
    PutActionStartedActionType,
    PutActionSucceededActionType,
} from "#store/actions/actionTypes";
import { ErrorPayload, GetState, isDone, ThunkResult } from "#store/types";
import { Dispatch } from "redux";
import { pilotService } from "#services";
import { fetchOperatorInvocationFromServer } from "#store/operatorInvocations";
import { isPresent } from "#types";
import { shouldRefetch } from "#/util/shouldRefetch";
import {
    Action,
    ActionType,
    DatasourceEnum,
} from "#services/pilotService/types";
import { push } from "redux-first-history";
import { generateUrl } from "#components/App/routing/generateUrl";
import { generatePath } from "react-router";
import { Routes } from "#/config";

/*******************************************************************************
 * actions
 ******************************************************************************/
// const actionKey = ();
export function fetchActionFromServerAndSetAsCurrent(id: number): ThunkResult {
    return (dispatch, getState) => {
        if (shouldRefetch(getState().actions.actions[id])) {
            dispatch(fetchActionStarted(id));

            // trigger
            pilotService
                .actionService(
                    getState().environment.SERVICE_URL,
                    getState().uiState.rid
                )
                .get(id)
                .then(result => {
                    dispatch(fetchActionSucceeded(result));
                    result.operatorInvocation.forEach(opId => {
                        dispatch(fetchOperatorInvocationFromServer(opId));
                    });
                })
                .catch(error => {
                    dispatch(
                        fetchActionFailed({
                            error,
                            id,
                        })
                    );
                });
        }
    };
}

export function listActionsFromServer(): ThunkResult {
    return (dispatch: Dispatch, getState: GetState) => {
        const ps = pilotService.actionService(
            getState().environment.SERVICE_URL,
            getState().uiState.rid
        );
        ps.list()
            .then((actions: Array<Action>) => {
                dispatch(
                    createAction(
                        ActionIdentifiers.FETCHED_LIST_FROM_SERVER,
                        actions
                    )
                );
                actions.forEach(r => {
                    dispatch(fetchActionSucceeded(r));
                });
            })
            .catch(err => {
                console.warn("Unable to fetch actions from server", err);
            });
    };
}

function fetchActionSucceeded(action: Action): FetchActionSucceededActionType {
    return createAction(ActionIdentifiers.FETCH_ACTION_SUCCEEDED, action);
}

function fetchActionFailed(error: ErrorPayload): FetchActionFailedActionType {
    return createAction(ActionIdentifiers.FETCH_ACTION_FAILED, error);
}

function fetchActionStarted(id: number): FetchActionStartedActionType {
    return createAction(ActionIdentifiers.FETCH_ACTION_STARTED, id);
}

function putQueryStarted(id: string): PutActionStartedActionType {
    return createAction(ActionIdentifiers.PUT_QUERY_STARTED, id);
}

export function putActionQuerySucceeded(
    result: Action
): PutActionSucceededActionType {
    return createAction(ActionIdentifiers.PUT_QUERY_SUCCEEDED, result);
}

function putQueryFailed(query: ErrorPayload): PutActionFailedActionType {
    return createAction(ActionIdentifiers.PUT_QUERY_FAILED, query);
}

export function triggerExploreByNl({
    query = "unknown",
    datasource = DatasourceEnum.cordis,
    maxInterpretations = 3,
    maxResultsPerInterpretation = 5,
    // nlToSqlInterpreter = ["valuenet", "nalir+", "soda"]
    nlToSqlInterpreter = ["nalir+", "soda"],
}: // nlToSqlInterpreter = ["valuenet"]
Partial<NlOperation>): ThunkResult {
    return (dispatch: Dispatch, getState: GetState) => {
        const sQuery = getState().uiState.query;
        const sDatasource = getState().uiState.selectedDatasource;
        const sMaxIn = getState().uiState.maxInterpretationsLimit;
        const sMaxRow = getState().uiState.rowsPerTableLimit;

        const data = {
            query: query ?? sQuery,
            datasource: datasource ?? sDatasource,
            maxInterpretations: maxInterpretations ?? sMaxIn,
            maxResultsPerInterpretation: maxResultsPerInterpretation ?? sMaxRow,
        } as NlOperation;

        const service = pilotService.actionService(
            getState().environment.SERVICE_URL,
            getState().uiState.rid
        );
        const key = data.datasource + data.query;
        const actionIsAlreadyDone = Object.values(getState().actions.actions)
            .filter(isDone)
            .map(x => x.payload)
            .filter(isPresent)
            .filter(x => x.query === data.query)
            .filter(
                x =>
                    x.actionType === ActionType.NL2SQL ||
                    x.actionType === ActionType.NL2SPARQL
            )
            .filter(x => x.datasource === data.datasource);

        const actionIsPending =
            Object.keys(getState().actions.actions).filter(x => x === key)
                .length > 0;
        if (!actionIsPending && actionIsAlreadyDone.length === 0) {
            dispatch(putQueryStarted(key));
            // the query has not yet been issued
            service
                .nl2ql(data.query, data.datasource, data.maxInterpretations)
                .then((action: Action) => {
                    dispatch(putActionQuerySucceeded(action));
                    // TODO start polling loops for all operator-invocations;
                    const operatorInvocations: Array<number> =
                        action.operatorInvocation;
                    operatorInvocations.forEach(oi => {
                        // TODO get operatorInvocations;
                        dispatch(fetchOperatorInvocationFromServer(oi));
                    });
                })
                .catch(error => {
                    dispatch(
                        putQueryFailed({
                            id: key,
                            error,
                            message:
                                "Unable to transmit nl2sql query successfully to server",
                        })
                    );
                });
        }
    };
}

export function triggerExploreByFilter(ops: FilterOperator): ThunkResult {
    return (dispatch, getState) => {
        const service = pilotService.actionService(
            getState().environment.SERVICE_URL,
            getState().uiState.rid
        );
        const sDatasource = getState().uiState.selectedDatasource;
        const sMaxIn = getState().uiState.maxInterpretationsLimit;
        const sMaxRow = getState().uiState.rowsPerTableLimit;

        const data = {
            datasource: sDatasource.name,
            maxInterpretations: sMaxIn,
            ...ops,
        };

        const key = data.datasource + data.query;
        const actionIsAlreadyDone = Object.values(getState().actions.actions)
            .filter(isDone)
            .map(x => x.payload)
            .filter(isPresent)
            .filter(x => x.query === data.query)
            .filter(x => x.actionType === ActionType.QL_FILTER)
            .filter(x => x.datasource === data.datasource);

        const actionIsPending =
            Object.keys(getState().actions.actions).filter(x => x === key)
                .length > 0;
        if (!actionIsPending && actionIsAlreadyDone.length === 0) {
            dispatch(putQueryStarted(key));
            // the query has not yet been issued

            service
                .exploreByFilter(data)
                .then(action => {
                    dispatch(putActionQuerySucceeded(action));
                    const operatorInvocations: Array<number> =
                        action.operatorInvocation;
                    operatorInvocations.forEach(oi => {
                        dispatch(fetchOperatorInvocationFromServer(oi));
                    });
                    dispatch(
                        push(
                            generatePath(Routes.action.path, {
                                id: String(action.id),
                            })
                        )
                    );
                })
                .catch(error => {
                    dispatch(
                        putQueryFailed({
                            id: key,
                            error,
                            message:
                                "Unable to transmit filter query successfully to server",
                        })
                    );
                });
        }
    };
}

export function triggerExploreByFacet(
    sql: unknown,
    cols: unknown
): ThunkResult {
    return (dispatch, getState) => {
        console.log("triggerExploreByFacet");
    };
}

export function triggerExploreBySuperset(sql: unknown): ThunkResult {
    return (dispatch, getState) => {
        console.log("triggerExploreBySuperset");
    };
}

export function triggerRecommendations(
    sql: unknown,
    cols: unknown,
    weights: unknown
): ThunkResult {
    return (dispatch, getState) => {
        console.log("triggerRecommendations");
    };
}

export function triggerGiveFeedback(feedback: unknown): ThunkResult {
    return (dispatch, getState) => {
        console.log("triggerGiveFeedback");
    };
}
